import {
  ClickAwayListener,
  Popper as MuiPopper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';
import type { ComponentType, MouseEventHandler, ReactElement } from 'react';
import { useState } from 'react';
import type {
  PopperContentProps,
  PopperProps,
  PopperTriggerProps,
} from './types';

const StyledMuiPopper = styled(MuiPopper, { name: 'StyledPopper' })(
  ({ theme }) => ({
    zIndex: theme.zIndex.modal,
  })
);

const Popper = <C extends PopperContentProps, T extends PopperTriggerProps>({
  badgeContent,
  placement = 'bottom-start',
  Content,
  Trigger,
  disabled,
  closeOnClickAway = true,
  onClose,
  strategy = 'fixed',
  contentProps,
  triggerProps,
}: PopperProps<C, T>): ReactElement => {
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const TriggerComponent = Trigger as ComponentType<T>;
  const ContentComponent = Content as ComponentType<C>;

  const open = (currentTarget: HTMLElement) => setAnchorEl(currentTarget);
  const close = () => {
    onClose?.();
    setAnchorEl(null);
  };

  const handleClickAway = () => {
    if (closeOnClickAway && isOpen) {
      close();
    }
  };

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    if (disabled) {
      return;
    }
    if (isOpen) {
      close();
    } else {
      open(event.currentTarget);
    }
  };

  const popperTriggerProps = {
    ...triggerProps,
    onClick: handleClick,
    ...(typeof disabled === 'boolean' && { disabled }),
    ...(badgeContent !== undefined && { badgeContent }),
  } as T;

  const popperContentProps = {
    ...contentProps,
    onClose: close,
  } as C;

  return (
    <>
      <TriggerComponent {...popperTriggerProps} />
      {isOpen && (
        // disableReactTree used to dismiss popper content when it contains <Slider>
        <ClickAwayListener onClickAway={handleClickAway} disableReactTree>
          <StyledMuiPopper
            open={isOpen}
            anchorEl={anchorEl}
            placement={placement}
            popperOptions={{ strategy }}
            modifiers={getModifiers(theme, small)}
          >
            <ContentComponent {...popperContentProps} />
          </StyledMuiPopper>
        </ClickAwayListener>
      )}
    </>
  );
};

export default Popper;

function getModifiers(theme: Theme, small: boolean) {
  return [
    {
      name: 'flip',
      enabled: true,
      options: {
        altBoundary: true,
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'preventOverflow',
      enabled: true,
      options: {
        altAxis: true,
        boundary: 'viewport',
        tether: false,
        padding: small ? 0 : 8,
      },
    },
    {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, Number.parseFloat(theme.spacing(1))],
      },
    },
  ];
}
